import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  getSellers, 
  approveSeller, 
  rejectSeller, 
  suspendSeller,
  getListings
} from '../../services/api';

function SellerDetail() {
  const { sellerId } = useParams();
  const navigate = useNavigate();
  const [seller, setSeller] = useState(null);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listingsLoading, setListingsLoading] = useState(false);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');

  useEffect(() => {
    fetchSellerDetail();
    fetchSellerListings();
  }, [sellerId]);

  const fetchSellerDetail = async () => {
    try {
      setLoading(true);
      const response = await getSellers({});
      const sellerData = response.data.sellers.find(s => s.seller_id == sellerId);
      
      if (!sellerData) {
        setError('Seller not found');
        return;
      }
      
      // Parse delivery_coverage_areas if it exists
      if (sellerData.delivery_coverage_areas && typeof sellerData.delivery_coverage_areas === 'string') {
        try {
          sellerData.delivery_coverage_areas = JSON.parse(sellerData.delivery_coverage_areas);
        } catch (err) {
          console.error('Failed to parse delivery coverage areas:', err);
          sellerData.delivery_coverage_areas = [];
        }
      } else if (!sellerData.delivery_coverage_areas) {
        sellerData.delivery_coverage_areas = [];
      }
      
      setSeller(sellerData);
    } catch (err) {
      setError('Failed to load seller details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchSellerListings = async () => {
    try {
      setListingsLoading(true);
      const response = await getListings({ seller_id: sellerId });
      setListings(response.data.listings || []);
    } catch (err) {
      console.error('Failed to load seller listings:', err);
    } finally {
      setListingsLoading(false);
    }
  };

  const handleSellerAction = async (sellerId, action, reason = '') => {
    let confirmationMessage = `Are you sure you want to ${action} this seller?`;
    
    if (action === 'reject' && !reason) {
      setShowRejectModal(true);
      return;
    }

    if (!window.confirm(confirmationMessage)) {
      return;
    }

    try {
      setActionLoading(true);
      
      switch (action) {
        case 'approve':
          await approveSeller(sellerId);
          break;
        case 'reject':
          await rejectSeller(sellerId, reason);
          setShowRejectModal(false);
          setRejectionReason('');
          break;
        case 'suspend':
          await suspendSeller(sellerId, reason);
          break;
        default:
          break;
      }
      
      await fetchSellerDetail();
    } catch (err) {
      setError(`Failed to ${action} seller. Please try again.`);
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    if (seller?.is_approved && status === 'active') {
      return (
        <div className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-green-900/30 text-green-400 border border-green-700/50">
          <div className="w-2 h-2 bg-green-400 rounded-full mr-2"></div>
          Active & Approved
        </div>
      );
    }
    
    if (!seller?.is_approved && status === 'active') {
      return (
        <div className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-yellow-900/30 text-yellow-400 border border-yellow-700/50 animate-pulse">
          <div className="w-2 h-2 bg-yellow-400 rounded-full mr-2"></div>
          Active - Pending Approval
        </div>
      );
    }
    
    switch (status) {
      case 'suspended':
        return (
          <div className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-red-900/30 text-red-400 border border-red-700/50">
            <div className="w-2 h-2 bg-red-400 rounded-full mr-2"></div>
            Suspended
          </div>
        );
      case 'inactive':
        return (
          <div className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-gray-700/50 text-gray-300 border border-gray-600/50">
            <div className="w-2 h-2 bg-gray-400 rounded-full mr-2"></div>
            Inactive
          </div>
        );
      default:
        return (
          <div className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-gray-700/50 text-gray-300 border border-gray-600/50">
            {status}
          </div>
        );
    }
  };

  const getVerificationBadge = (isVerified) => {
    return isVerified ? (
      <div className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-green-900/30 text-green-400 border border-green-700/50">
        <span className="mr-1.5">✓</span>
        Verified
      </div>
    ) : (
      <div className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-yellow-900/30 text-yellow-400 border border-yellow-700/50">
        <span className="mr-1.5">⚠</span>
        Unverified
      </div>
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN'
    }).format(amount || 0);
  };

  const formatTime = (timeString) => {
    if (!timeString) return 'N/A';
    return new Date(`1970-01-01T${timeString}`).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const getDeliveryOptionsBadge = (option) => {
    switch (option) {
      case 'pickup':
        return (
          <div className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-900/30 text-blue-400 border border-blue-700/50">
            Pickup Only
          </div>
        );
      case 'delivery':
        return (
          <div className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-purple-900/30 text-purple-400 border border-purple-700/50">
            Delivery Only
          </div>
        );
      case 'both':
        return (
          <div className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-green-900/30 text-green-400 border border-green-700/50">
            Pickup & Delivery
          </div>
        );
      default:
        return (
          <div className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-700/50 text-gray-300 border border-gray-600/50">
            {option}
          </div>
        );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#ff660e] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-300">Loading seller details...</p>
        </div>
      </div>
    );
  }

  if (error && !seller) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="text-center">
          <div className="text-red-400 text-6xl mb-4">❌</div>
          <h3 className="text-xl font-semibold text-gray-300 mb-2">Error Loading Seller</h3>
          <p className="text-gray-500 mb-6">{error}</p>
          <button
            onClick={() => navigate('/admin/sellers')}
            className="bg-gradient-to-r from-[#ff660e] to-orange-500 text-white px-6 py-3 rounded-lg font-semibold hover:from-[#ff5500] hover:to-orange-600 transition-all duration-200 hover:scale-[1.02] shadow-lg"
          >
            ← Back to Sellers
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Header */}
      <div className="bg-gray-800 shadow-lg border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:justify-between md:items-center py-6 space-y-4 md:space-y-0">
            <div>
              <div className="flex items-center space-x-2 mb-2">
                <Link 
                  to="/admin/sellers"
                  className="text-blue-400 hover:text-blue-300 transition-colors flex items-center space-x-2"
                >
                  <span>←</span>
                  <span>Back to Sellers</span>
                </Link>
              </div>
              <h1 className="text-3xl font-bold text-white">
                Seller <span className="text-[#ff660e]">Details</span>
              </h1>
              <p className="text-gray-400 mt-1">Manage seller account and store information</p>
            </div>
            
            {/* Action Buttons */}
            <div className="flex space-x-3">
              {!seller?.is_approved && seller?.status === 'active' && (
                <>
                  <button
                    onClick={() => handleSellerAction(seller.user_id, 'approve')}
                    disabled={actionLoading}
                    className="bg-gradient-to-r from-green-600 to-emerald-500 text-white px-6 py-3 rounded-lg font-semibold hover:from-green-700 hover:to-emerald-600 transition-all duration-200 hover:scale-[1.02] shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                  >
                    {actionLoading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>Approving...</span>
                      </>
                    ) : (
                      <>
                        <span>✅</span>
                        <span>Approve Seller</span>
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => setShowRejectModal(true)}
                    disabled={actionLoading}
                    className="bg-gradient-to-r from-red-600 to-red-500 text-white px-6 py-3 rounded-lg font-semibold hover:from-red-700 hover:to-red-600 transition-all duration-200 hover:scale-[1.02] shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                  >
                    <span>❌</span>
                    <span>Reject Seller</span>
                  </button>
                </>
              )}
              {seller?.is_approved && seller?.status === 'active' && (
                <button
                  onClick={() => handleSellerAction(seller.user_id, 'suspend', 'Administrative action')}
                  disabled={actionLoading}
                  className="bg-gradient-to-r from-red-600 to-red-500 text-white px-6 py-3 rounded-lg font-semibold hover:from-red-700 hover:to-red-600 transition-all duration-200 hover:scale-[1.02] shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  {actionLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Suspending...</span>
                    </>
                  ) : (
                    <>
                      <span>⏸️</span>
                      <span>Suspend Seller</span>
                    </>
                  )}
                </button>
              )}
              {seller?.status === 'suspended' && (
                <button
                onClick={() => handleSellerAction(seller.user_id, 'approve')}
                  disabled={actionLoading}
                  className="bg-gradient-to-r from-green-600 to-emerald-500 text-white px-6 py-3 rounded-lg font-semibold hover:from-green-700 hover:to-emerald-600 transition-all duration-200 hover:scale-[1.02] shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  {actionLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Activating...</span>
                    </>
                  ) : (
                    <>
                      <span>✅</span>
                      <span>Activate Seller</span>
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
          <div className="bg-red-900/20 border border-red-700 rounded-lg p-4 flex items-center justify-between backdrop-blur-sm">
            <div className="flex items-center space-x-3">
              <span className="text-red-400 text-xl">⚠️</span>
              <p className="text-red-300">{error}</p>
            </div>
            <button 
              onClick={() => setError('')}
              className="text-red-400 hover:text-red-300 font-medium transition-colors"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Main Content - 3/4 width */}
          <div className="lg:col-span-3 space-y-8">
            {/* Store Overview Card */}
            <div className="bg-gray-800 rounded-xl shadow-lg border border-gray-700 p-6">
              <div className="flex items-start justify-between mb-6">
                <div className="flex items-center space-x-4">
                  {seller?.store_logo_url ? (
                    <img
                      className="h-20 w-20 rounded-xl object-cover ring-2 ring-gray-700"
                      src={seller.store_logo_url}
                      alt={seller.store_name}
                    />
                  ) : (
                    <div className="h-20 w-20 bg-gradient-to-br from-[#ff660e] to-orange-400 rounded-xl flex items-center justify-center text-white font-bold text-2xl ring-2 ring-gray-700">
                      {seller?.store_name?.[0]}
                    </div>
                  )}
                  <div>
                    <h2 className="text-2xl font-bold text-white">{seller?.store_name}</h2>
                    <p className="text-gray-400">{seller?.email}</p>
                    <div className="flex items-center space-x-3 mt-3">
                      {getStatusBadge(seller?.status)}
                      <span className="text-sm text-gray-500">
                        Seller ID: {seller?.seller_id}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Store Information */}
                <div>
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                    <span className="mr-2 text-[#ff660e]">🏪</span>
                    Store Information
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium text-gray-400">Store Name</label>
                      <p className="text-white text-lg">{seller?.store_name}</p>
                    </div>
                    {seller?.store_description && (
                      <div>
                        <label className="text-sm font-medium text-gray-400">Description</label>
                        <p className="text-white">{seller.store_description}</p>
                      </div>
                    )}
                    {seller?.store_location && (
                      <div>
                        <label className="text-sm font-medium text-gray-400">Address</label>
                        <p className="text-white">{seller.store_location}</p>
                      </div>
                    )}
                    {(seller?.state_name || seller?.lga_name || seller?.lcda_name) && (
                      <div>
                        <label className="text-sm font-medium text-gray-400">Location</label>
                        <p className="text-white">
                          {[seller?.state_name, seller?.lga_name, seller?.lcda_name].filter(Boolean).join(', ')}
                        </p>
                      </div>
                    )}
                    
                    {/* Store Hours */}
                    <div className="border-t border-gray-700 pt-4 mt-4">
                      <label className="text-sm font-medium text-gray-400 mb-2 block">Store Hours</label>
                      {seller?.is_24_hours ? (
                        <div className="flex items-center space-x-2 text-green-400">
                          <span>🕒</span>
                          <span className="font-medium">24/7 Open</span>
                        </div>
                      ) : seller?.opening_time && seller?.closing_time ? (
                        <div className="flex justify-between items-center">
                          <div>
                            <span className="text-sm text-gray-400">Open:</span>
                            <span className="ml-2 text-white">{formatTime(seller.opening_time)}</span>
                          </div>
                          <div>
                            <span className="text-sm text-gray-400">Close:</span>
                            <span className="ml-2 text-white">{formatTime(seller.closing_time)}</span>
                          </div>
                        </div>
                      ) : (
                        <p className="text-gray-500 text-sm">Not specified</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Seller Information */}
                <div>
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                    <span className="mr-2 text-[#ff660e]">👤</span>
                    Seller Information
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium text-gray-400">Seller Name</label>
                      <p className="text-white text-lg">
                        {seller?.first_name} {seller?.last_name}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-400">Email</label>
                      <p className="text-white">{seller?.email}</p>
                      <div className="mt-2">
                        {getVerificationBadge(seller?.is_email_verified)}
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-400">Phone Number</label>
                      <p className="text-white">{seller?.phone_number || seller?.user_phone}</p>
                      <div className="mt-2">
                        {getVerificationBadge(seller?.is_phone_verified)}
                      </div>
                    </div>
                    
                    {/* Delivery Information */}
                    <div className="border-t border-gray-700 pt-4 mt-4">
                      <label className="text-sm font-medium text-gray-400 mb-2 block">Delivery Information</label>
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-400">Delivery Options:</span>
                          {getDeliveryOptionsBadge(seller?.delivery_options)}
                        </div>
                        {seller?.delivery_fee && (
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-400">Delivery Fee:</span>
                            <span className="text-white font-medium">{formatCurrency(seller.delivery_fee)}</span>
                          </div>
                        )}
                        {seller?.delivery_coverage_areas && seller.delivery_coverage_areas.length > 0 && (
                          <div>
                            <span className="text-sm text-gray-400">Coverage Areas:</span>
                            <div className="flex flex-wrap gap-2 mt-2">
                              {seller.delivery_coverage_areas.map((area, index) => (
                                <span key={index} className="px-2 py-1 text-xs bg-gray-700/50 text-gray-300 rounded">
                                  {area}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Store Media & Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Store Media */}
              <div className="bg-gray-800 rounded-xl shadow-lg border border-gray-700 p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                  <span className="mr-2 text-[#ff660e]">📷</span>
                  Store Media
                </h3>
                <div className="space-y-4">
                  {seller?.store_cover_picture_url && (
                    <div>
                      <label className="text-sm font-medium text-gray-400 mb-2 block">Store Cover Picture</label>
                      <img
                        src={seller.store_cover_picture_url}
                        alt="Store Cover"
                        className="w-full h-40 object-cover rounded-lg border border-gray-700"
                      />
                    </div>
                  )}
                  {seller?.store_profile_picture_url && (
                    <div>
                      <label className="text-sm font-medium text-gray-400 mb-2 block">Store Profile Picture</label>
                      <img
                        src={seller.store_profile_picture_url}
                        alt="Store Profile"
                        className="w-32 h-32 object-cover rounded-lg border border-gray-700"
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Performance Metrics */}
              <div className="bg-gray-800 rounded-xl shadow-lg border border-gray-700 p-6">
                <h3 className="text-lg font-semibold text-white mb-6 flex items-center">
                  <span className="mr-2 text-[#ff660e]">📊</span>
                  Performance Metrics
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-4 bg-blue-900/20 border border-blue-700/30 rounded-xl">
                    <div className="text-2xl font-bold text-blue-400">{seller?.active_listings || 0}</div>
                    <div className="text-sm text-blue-400">Active Listings</div>
                  </div>
                  <div className="text-center p-4 bg-green-900/20 border border-green-700/30 rounded-xl">
                    <div className="text-2xl font-bold text-green-400">{seller?.completed_orders || 0}</div>
                    <div className="text-sm text-green-400">Completed Orders</div>
                  </div>
                  <div className="text-center p-4 bg-purple-900/20 border border-purple-700/30 rounded-xl">
                    <div className="text-2xl font-bold text-purple-400">
                      {seller?.rating_avg && typeof seller.rating_avg === 'number' ? seller.rating_avg.toFixed(1) : 'N/A'}
                    </div>
                    <div className="text-sm text-purple-400">Seller Rating</div>
                  </div>
                  <div className="text-center p-4 bg-[#ff660e]/10 border border-[#ff660e]/30 rounded-xl">
                    <div className="text-2xl font-bold text-[#ff660e]">
                      {seller?.total_sales ? formatCurrency(seller.total_sales) : '₦0'}
                    </div>
                    <div className="text-sm text-[#ff660e]">Total Sales</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Timeline Information */}
            <div className="bg-gray-800 rounded-xl shadow-lg border border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                <span className="mr-2 text-[#ff660e]">📅</span>
                Timeline
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-gray-750 border border-gray-600 rounded-lg p-4">
                  <div className="text-sm font-medium text-gray-400 mb-1">User Joined</div>
                  <div className="text-white">
                    {new Date(seller?.user_joined).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </div>
                </div>
                <div className="bg-gray-750 border border-gray-600 rounded-lg p-4">
                  <div className="text-sm font-medium text-gray-400 mb-1">Store Created</div>
                  <div className="text-white">
                    {new Date(seller?.created_at).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </div>
                </div>
                <div className="bg-gray-750 border border-gray-600 rounded-lg p-4">
                  <div className="text-sm font-medium text-gray-400 mb-1">Last Updated</div>
                  <div className="text-white">
                    {new Date(seller?.updated_at).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Listings */}
            <div className="bg-gray-800 rounded-xl shadow-lg border border-gray-700 p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-white flex items-center">
                  <span className="mr-2 text-[#ff660e]">📦</span>
                  Recent Listings
                </h3>
                <button
                  onClick={() => navigate(`/admin/listings?seller_id=${sellerId}`)}
                  className="text-blue-400 hover:text-blue-300 text-sm font-medium flex items-center space-x-2 hover:scale-105 transition-transform"
                >
                  <span>View All Listings</span>
                  <span>→</span>
                </button>
              </div>

              {listingsLoading ? (
                <div className="text-center py-8">
                  <div className="w-8 h-8 border-2 border-[#ff660e] border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                  <p className="text-gray-400">Loading listings...</p>
                </div>
              ) : listings.length > 0 ? (
                <div className="space-y-4">
                  {listings.slice(0, 5).map((listing) => (
                    <div key={listing.listing_id} className="flex items-center justify-between p-4 bg-gray-750 border border-gray-700 rounded-xl hover:bg-gray-700 transition-all duration-200">
                      <div className="flex items-center space-x-4">
                        {listing.images?.[0] ? (
                          <img
                            src={listing.images[0]}
                            alt={listing.title}
                            className="w-12 h-12 rounded-lg object-cover ring-1 ring-gray-600"
                          />
                        ) : (
                          <div className="w-12 h-12 bg-gradient-to-br from-[#ff660e]/20 to-orange-400/20 rounded-lg flex items-center justify-center text-gray-400">
                            📦
                          </div>
                        )}
                        <div>
                          <h4 className="font-semibold text-white">{listing.title}</h4>
                          <p className="text-sm text-gray-400">{formatCurrency(listing.price)}</p>
                          <div className="flex items-center space-x-2 mt-1">
                            <span className={`px-2 py-1 text-xs rounded-full ${
                              listing.status === 'active' ? 'bg-green-900/30 text-green-400 border border-green-700/50' :
                              listing.status === 'pending' ? 'bg-yellow-900/30 text-yellow-400 border border-yellow-700/50' :
                              'bg-gray-700/50 text-gray-300 border border-gray-600/50'
                            }`}>
                              {listing.status}
                            </span>
                            {listing.is_featured && (
                              <span className="px-2 py-1 bg-purple-900/30 text-purple-400 text-xs rounded-full border border-purple-700/50">
                                Featured
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => navigate(`/admin/listings/${listing.listing_id}`)}
                        className="text-blue-400 hover:text-blue-300 text-sm font-medium hover:scale-105 transition-transform"
                      >
                        View
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 border border-gray-700 rounded-xl">
                  <div className="text-gray-400 text-4xl mb-2">📦</div>
                  <p className="text-gray-500">No listings found for this seller</p>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar - 1/4 width */}
          <div className="space-y-8">
            {/* Account Status */}
            <div className="bg-gray-800 rounded-xl shadow-lg border border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-white mb-6 flex items-center">
                <span className="mr-2 text-[#ff660e]">📋</span>
                Account Status
              </h3>
              <div className="space-y-4">
                <div>
                  <span className="text-sm font-medium text-gray-400">Store Status</span>
                  <div className="mt-2">{getStatusBadge(seller?.status)}</div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-400">Approval Status</span>
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    seller?.is_approved 
                      ? 'bg-green-900/30 text-green-400 border border-green-700/50' 
                      : 'bg-yellow-900/30 text-yellow-400 border border-yellow-700/50'
                  }`}>
                    {seller?.is_approved ? 'Approved' : 'Pending'}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-400">Email Verified</span>
                  {getVerificationBadge(seller?.is_email_verified)}
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-400">Phone Verified</span>
                  {getVerificationBadge(seller?.is_phone_verified)}
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-400">24/7 Operation</span>
                  <p className="text-sm text-white mt-1">
                    {seller?.is_24_hours ? 'Yes' : 'No'}
                  </p>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-gray-800 rounded-xl shadow-lg border border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-white mb-6 flex items-center">
                <span className="mr-2 text-[#ff660e]">⚡</span>
                Quick Actions
              </h3>
              <div className="space-y-3">
                <button
                  onClick={() => navigate(`/admin/users/${seller?.user_id}`)}
                  className="w-full text-left px-4 py-3 text-sm text-gray-300 hover:bg-gray-750 hover:text-white rounded-lg transition-all duration-200 hover:scale-[1.02] border border-gray-700 hover:border-[#ff660e] flex items-center space-x-3"
                >
                  <span className="text-lg">👤</span>
                  <span>View User Profile</span>
                </button>
                <button
                  onClick={() => navigate(`/admin/orders?seller_id=${sellerId}`)}
                  className="w-full text-left px-4 py-3 text-sm text-gray-300 hover:bg-gray-750 hover:text-white rounded-lg transition-all duration-200 hover:scale-[1.02] border border-gray-700 hover:border-[#ff660e] flex items-center space-x-3"
                >
                  <span className="text-lg">📦</span>
                  <span>View All Orders</span>
                </button>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(sellerId);
                    alert('Seller ID copied to clipboard!');
                  }}
                  className="w-full text-left px-4 py-3 text-sm text-gray-300 hover:bg-gray-750 hover:text-white rounded-lg transition-all duration-200 hover:scale-[1.02] border border-gray-700 hover:border-[#ff660e] flex items-center space-x-3"
                >
                  <span className="text-lg">📋</span>
                  <span>Copy Seller ID</span>
                </button>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(seller?.email);
                    alert('Email copied to clipboard!');
                  }}
                  className="w-full text-left px-4 py-3 text-sm text-gray-300 hover:bg-gray-750 hover:text-white rounded-lg transition-all duration-200 hover:scale-[1.02] border border-gray-700 hover:border-[#ff660e] flex items-center space-x-3"
                >
                  <span className="text-lg">✉️</span>
                  <span>Copy Email</span>
                </button>
                <button
                  onClick={() => {
                    const sellerData = {
                      SellerID: seller?.seller_id,
                      UserID: seller?.user_id,
                      StoreName: seller?.store_name,
                      Email: seller?.email,
                      Phone: seller?.phone_number,
                      Status: seller?.status,
                      IsApproved: seller?.is_approved,
                      Rating: seller?.rating_avg,
                      TotalSales: seller?.total_sales,
                      Created: seller?.created_at
                    };
                    navigator.clipboard.writeText(JSON.stringify(sellerData, null, 2));
                    alert('Seller data copied to clipboard!');
                  }}
                  className="w-full text-left px-4 py-3 text-sm text-gray-300 hover:bg-gray-750 hover:text-white rounded-lg transition-all duration-200 hover:scale-[1.02] border border-gray-700 hover:border-[#ff660e] flex items-center space-x-3"
                >
                  <span className="text-lg">📄</span>
                  <span>Copy Seller Data</span>
                </button>
              </div>
            </div>

            {/* Store Statistics */}
            <div className="bg-gray-800 rounded-xl shadow-lg border border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-white mb-6 flex items-center">
                <span className="mr-2 text-[#ff660e]">📈</span>
                Store Statistics
              </h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-400">Listings</span>
                  <span className="font-semibold text-white">{seller?.active_listings || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-400">Sales</span>
                  <span className="font-semibold text-white">{seller?.completed_orders || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-400">Rating</span>
                  <span className="font-semibold text-yellow-400">
                    {seller?.rating_avg && typeof seller.rating_avg === 'number' ? seller.rating_avg.toFixed(1) : 'N/A'} ⭐
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-400">Revenue</span>
                  <span className="font-semibold text-green-400">
                    {seller?.total_sales ? formatCurrency(seller.total_sales) : '₦0'}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-400">Delivery Fee</span>
                  <span className="font-semibold text-blue-400">
                    {seller?.delivery_fee ? formatCurrency(seller.delivery_fee) : 'N/A'}
                  </span>
                </div>
              </div>
            </div>

            {/* ID Information */}
            <div className="bg-gray-800 rounded-xl shadow-lg border border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-white mb-6 flex items-center">
                <span className="mr-2 text-[#ff660e]">🆔</span>
                ID Information
              </h3>
              <div className="space-y-3">
                <div className="bg-gray-750 border border-gray-600 rounded p-3">
                  <div className="text-xs text-gray-400">Seller ID</div>
                  <div className="text-sm font-mono text-white">{seller?.seller_id}</div>
                </div>
                <div className="bg-gray-750 border border-gray-600 rounded p-3">
                  <div className="text-xs text-gray-400">User ID</div>
                  <div className="text-sm font-mono text-white">{seller?.user_id}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Rejection Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-gray-800 rounded-xl border border-gray-700 max-w-md w-full p-6 shadow-2xl">
            <h3 className="text-xl font-bold text-white mb-4 flex items-center">
              <span className="text-red-400 mr-2">❌</span>
              Reject Seller Application
            </h3>
            <p className="text-gray-400 mb-6">Please provide a reason for rejecting this seller application:</p>
            
            <textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Enter rejection reason..."
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:ring-2 focus:ring-[#ff660e] focus:border-transparent transition-all mb-6"
              rows="4"
            />
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectionReason('');
                }}
                className="px-5 py-2.5 text-gray-300 border border-gray-600 rounded-lg hover:bg-gray-700 hover:text-white transition-all duration-200"
              >
                Cancel
              </button>
              <button
                onClick={() => handleSellerAction(seller.user_id,'reject', rejectionReason)}
                disabled={!rejectionReason.trim() || actionLoading}
                className="px-5 py-2.5 bg-gradient-to-r from-red-600 to-red-500 text-white rounded-lg font-semibold hover:from-red-700 hover:to-red-600 transition-all duration-200 hover:scale-[1.02] shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {actionLoading ? (
                  <span className="flex items-center space-x-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Rejecting...</span>
                  </span>
                ) : 'Reject Application'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SellerDetail;